import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme, isDarkModeSupported } from '../../context/ThemeContext';
import { useSettings } from '../../context/SettingsContext';
import NotificationDropdown from '../Notifications/NotificationDropdown';
import './Navbar.css';

// Navigation links for landing page (marketing)
const landingNavLinks = [
  { id: 'home', label: 'Home', path: '/', isHash: false },
  { id: 'services', label: 'Services', path: '/#services', isHash: true },
  { id: 'about', label: 'About', path: '/#about', isHash: true },
  { id: 'projects', label: 'Projects', path: '/#projects', isHash: true },
  { id: 'contact', label: 'Contact', path: '/#contact', isHash: true }
];

// Navigation links for shop context
const shopNavLinks = [
  { id: 'shop', label: 'All Products', path: '/shop', isHash: false },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const { settings } = useSettings();
  const catalogSettings = settings?.catalog || {};
  const showPriceBanner = settings ? catalogSettings.hidePrices !== false : false;
  const priceDisclaimer = catalogSettings.priceDisclaimer ||
    'Due to current market uncertainty, prices may vary. Final pricing will be confirmed upon request or following a site visit.';
  
  // Check if we're on the home page
  const isHomePage = location.pathname === '/';
  
  // Check if dark mode toggle should be shown on this route
  const showThemeToggle = isDarkModeSupported(location.pathname);
  
  // Determine navigation context
  const isAdminPage = location.pathname.startsWith('/admin');
  const isDashboardPage = location.pathname.startsWith('/dashboard');
  const isShopPage = location.pathname.startsWith('/shop') || 
                     location.pathname.startsWith('/product');
  
  // Get appropriate nav links based on context
  const getNavLinks = () => {
    if (isAdminPage || isDashboardPage) {
      return []; // Admin and dashboard have their own sidebars
    }
    if (isShopPage) {
      return shopNavLinks;
    }
    return landingNavLinks;
  };
  
  const navLinks = getNavLinks();
  
  // Pages that need dark navbar (light background pages)
  const needsDarkNavbar = ['/shop', '/dashboard', '/notifications', '/admin'].some(
    path => location.pathname.startsWith(path)
  );

  useEffect(() => {
    const handleScroll = () => {
      // Handle navbar background
      setIsScrolled(window.scrollY > 50);

      // Only track sections on home page
      if (!isHomePage) return;

      // Determine active section based on scroll position
      const sectionIds = landingNavLinks.filter(l => l.isHash).map(l => l.id);
      const scrollPosition = window.scrollY + 150;

      for (let i = sectionIds.length - 1; i >= 0; i--) {
        const section = document.getElementById(sectionIds[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sectionIds[i]);
          return;
        }
      }
      setActiveSection('home');
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  useEffect(() => {
    document.body.classList.toggle('price-banner-active', showPriceBanner);
    return () => document.body.classList.remove('price-banner-active');
  }, [showPriceBanner]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isUserMenuOpen && !e.target.closest('.navbar__user')) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isUserMenuOpen]);

  // Close mobile menu and handle navigation
  const handleNavClick = (link) => {
    setActiveSection(link.id);
    setIsMobileMenuOpen(false);
    
    // If we're on home page and it's a hash link, scroll to section
    if (isHomePage && link.isHash) {
      const element = document.getElementById(link.id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleOverlayClick = () => {
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  // Determine if a link is active
  const isLinkActive = (link) => {
    // For hash links (landing page sections)
    if (link.isHash) {
      if (!isHomePage) return false;
      return activeSection === link.id;
    }
    // For regular links, check if current path matches
    return location.pathname === link.path || location.pathname.startsWith(link.path + '/');
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user) return '';
    const first = user.firstName?.charAt(0) || '';
    const last = user.lastName?.charAt(0) || '';
    return (first + last).toUpperCase();
  };

  // Get logo destination based on auth status
  const getLogoDestination = () => {
    if (!isAuthenticated) return '/';
    return user?.role === 'admin' ? '/admin' : '/dashboard';
  };

  return (
    <>
    {showPriceBanner && (
      <div className="navbar__price-banner" role="status">
        {priceDisclaimer}
      </div>
    )}
    <nav className={`navbar ${isScrolled || needsDarkNavbar ? 'navbar--scrolled' : ''} ${showPriceBanner ? 'navbar--with-price-banner' : ''}`}>
      {/* Mobile menu overlay */}
      <div 
        className={`navbar__overlay ${isMobileMenuOpen ? 'navbar__overlay--visible' : ''}`}
        onClick={handleOverlayClick}
      ></div>

      <div className="navbar__container">
        <Link to={getLogoDestination()} className="navbar__logo" onClick={() => handleNavClick({ id: 'home' })}>
          <img 
            src="/images/logo.png" 
            alt="London Builders" 
            className="navbar__logo-img"
          />
        </Link>

        <ul className={`navbar__menu ${isMobileMenuOpen ? 'navbar__menu--open' : ''}`}>
          {/* Show Back to Home link on admin/dashboard pages */}
          {(isAdminPage || isDashboardPage) && (
            <li>
              <Link 
                to="/"
                className="navbar__link navbar__link--back"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                ← Home
              </Link>
            </li>
          )}
          
          {/* Context-based navigation links */}
          {navLinks.map(link => (
            <li key={link.id}>
              {link.isHash ? (
                <a 
                  href={link.path}
                  className={`navbar__link ${isLinkActive(link) ? 'navbar__link--active' : ''}`}
                  onClick={(e) => {
                    if (isHomePage) {
                      e.preventDefault();
                    }
                    handleNavClick(link);
                  }}
                >
                  {link.label}
                </a>
              ) : (
                <Link 
                  to={link.path}
                  className={`navbar__link ${isLinkActive(link) ? 'navbar__link--active' : ''}`}
                  onClick={() => handleNavClick(link)}
                >
                  {link.label}
                </Link>
              )}
            </li>
          ))}
          
          {/* Mobile-only menu items */}
          {!isShopPage && (
            <li className="navbar__menu-cta">
              <Link to="/shop" className="navbar__cta" onClick={() => setIsMobileMenuOpen(false)}>
                Shop
              </Link>
            </li>
          )}
          {isAuthenticated ? (
            <>
              {user?.role === 'admin' && !isAdminPage && (
                <li className="navbar__menu-cta">
                  <Link to="/admin" className="navbar__cta navbar__cta--admin" onClick={() => setIsMobileMenuOpen(false)}>
                    Admin Panel
                  </Link>
                </li>
              )}
              {!isDashboardPage && (
                <li className="navbar__menu-cta">
                  <Link to="/dashboard" className="navbar__cta" onClick={() => setIsMobileMenuOpen(false)}>
                    Dashboard
                  </Link>
                </li>
              )}
              <li className="navbar__menu-cta navbar__menu-cta--secondary">
                <button onClick={handleLogout} className="navbar__logout-btn">
                  Logout
                </button>
              </li>
            </>
          ) : (
            <li className="navbar__menu-cta">
              <Link to="/login" className="navbar__cta" onClick={() => setIsMobileMenuOpen(false)}>
                Login
              </Link>
            </li>
          )}
        </ul>

        <div className="navbar__actions">
          {isAuthenticated ? (
            <>
              {/* Icon action buttons for logged-in users */}
              <div className="navbar__icon-actions">
                {showThemeToggle && (
                  <button 
                    className="navbar__icon-btn navbar__theme-toggle" 
                    onClick={toggleTheme}
                    title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                  >
                    {isDarkMode ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="5"/>
                        <line x1="12" y1="1" x2="12" y2="3"/>
                        <line x1="12" y1="21" x2="12" y2="23"/>
                        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                        <line x1="1" y1="12" x2="3" y2="12"/>
                        <line x1="21" y1="12" x2="23" y2="12"/>
                        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                      </svg>
                    )}
                  </button>
                )}
                <Link to="/shop" className="navbar__icon-btn" title="Shop">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="9" cy="21" r="1"/>
                    <circle cx="20" cy="21" r="1"/>
                    <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
                  </svg>
                </Link>
                <div className="navbar__notifications">
                  <NotificationDropdown />
                </div>
              </div>
              <div className="navbar__user">
                <button 
                  className="navbar__user-btn"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                >
                  <span className="navbar__user-avatar">{getUserInitials()}</span>
                  <span className="navbar__user-name">{user?.firstName}</span>
                <svg 
                  className={`navbar__user-arrow ${isUserMenuOpen ? 'navbar__user-arrow--open' : ''}`}
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              
              {isUserMenuOpen && (
                <div className="navbar__user-menu">
                  <div className="navbar__user-info">
                    <span className="navbar__user-fullname">{user?.firstName} {user?.lastName}</span>
                    <span className="navbar__user-email">{user?.email}</span>
                    {user?.role === 'admin' && (
                      <span className="navbar__user-badge">Admin</span>
                    )}
                  </div>
                  <div className="navbar__user-divider"></div>
                  <Link to="/dashboard" className="navbar__user-link" onClick={() => setIsUserMenuOpen(false)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="7" height="7"/>
                      <rect x="14" y="3" width="7" height="7"/>
                      <rect x="14" y="14" width="7" height="7"/>
                      <rect x="3" y="14" width="7" height="7"/>
                    </svg>
                    My Dashboard
                  </Link>
                  {user?.role === 'admin' && (
                    <Link to="/admin" className="navbar__user-link navbar__user-link--admin" onClick={() => setIsUserMenuOpen(false)}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 15a3 3 0 100-6 3 3 0 000 6z"/>
                        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
                      </svg>
                      Admin Panel
                    </Link>
                  )}
                  <div className="navbar__user-divider"></div>
                  <button className="navbar__user-link navbar__user-link--logout" onClick={handleLogout}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                      <polyline points="16 17 21 12 16 7"/>
                      <line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                    Logout
                  </button>
                </div>
              )}
            </div>
            </>
          ) : (
            <>
              {showThemeToggle && (
                <button 
                  className="navbar__icon-btn navbar__theme-toggle" 
                  onClick={toggleTheme}
                  title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                >
                  {isDarkMode ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="5"/>
                      <line x1="12" y1="1" x2="12" y2="3"/>
                      <line x1="12" y1="21" x2="12" y2="23"/>
                      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                      <line x1="1" y1="12" x2="3" y2="12"/>
                      <line x1="21" y1="12" x2="23" y2="12"/>
                      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                    </svg>
                  )}
                </button>
              )}
              <Link to="/shop" className="navbar__shop-link">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="9" cy="21" r="1"/>
                  <circle cx="20" cy="21" r="1"/>
                  <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
                </svg>
                <span>Shop</span>
              </Link>
              <Link to="/login" className="navbar__cta" onClick={() => setIsMobileMenuOpen(false)}>
                Login
              </Link>
            </>
          )}
        </div>

        <button 
          className={`navbar__hamburger ${isMobileMenuOpen ? 'navbar__hamburger--active' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </nav>
    </>
  );
};

export default Navbar;
